import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { RotateCw, MapPin, X } from "lucide-react";
import { toast } from "sonner";

const NUDGE_DISMISS_KEY = "willoah.landing.location-nudge.dismissed.v1";

export default function Landing() {
  const { user, refresh } = useAuth();
  const [featured, setFeatured] = useState(null);
  const [stats, setStats] = useState(null);
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1e9));
  const [recentIds, setRecentIds] = useState([]); // rotate anti-repeat
  const [locationDismissed, setLocationDismissed] = useState(() => {
    try { return !!localStorage.getItem(NUDGE_DISMISS_KEY); } catch { return false; }
  });
  const [locationDraft, setLocationDraft] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const showLocationNudge = !!user && !user.location && !locationDismissed;

  const dismissNudge = () => {
    setLocationDismissed(true);
    try { localStorage.setItem(NUDGE_DISMISS_KEY, "1"); } catch {}
  };

  const saveLocation = async () => {
    const loc = locationDraft.trim();
    if (!loc) return toast("Type your city first.");
    setSavingLocation(true);
    try {
      await api.patch("/users/me", { location: loc });
      toast("Location saved. The hero is a little more local now.");
      dismissNudge();
      refresh?.();
      // Pass loc explicitly — the AuthContext state hasn't settled yet on
      // this same tick, so relying on user.location would omit near=.
      fetchFeatured(seed, recentIds, loc);
    } catch (e) {
      toast(e?.response?.data?.detail || "Could not save location.");
    } finally {
      setSavingLocation(false);
    }
  };

  const fetchFeatured = useCallback(async (nextSeed, excludeIds, nearOverride) => {
    try {
      const params = { seed: nextSeed };
      if (excludeIds?.length) params.exclude = excludeIds.join(",");
      // Bias only when the visitor has set their own location. Never uses
      // any contributor's private location. `nearOverride` lets callers
      // pass a freshly-typed value before the AuthContext state settles.
      const near = nearOverride ?? user?.location;
      if (near) params.near = near;
      const { data } = await api.get("/landing/featured", { params });
      setFeatured(data);
      if (data?.branch?.id) {
        setRecentIds((prev) => {
          const next = [data.branch.id, ...prev.filter((x) => x !== data.branch.id)];
          return next.slice(0, 6); // remember last 6 shown
        });
      }
    } catch {
      setFeatured({ branch: null, quote: null });
    }
  }, [user?.location]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/landing/stats");
        setStats(data);
      } catch {
        setStats({ branches: 0, field_notes: 0, sharers: 0 });
      }
    })();
    fetchFeatured(seed, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard rotate: press → to spin the hero
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const t = e.target;
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
        rotate();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentIds]);

  const rotate = () => {
    const nextSeed = Math.floor(Math.random() * 1e9);
    setSeed(nextSeed);
    fetchFeatured(nextSeed, recentIds);
  };

  const branch = featured?.branch || null;
  const quote = featured?.quote || null;
  // Only render the "a real branch, right now" preview when the API actually
  // returned a real branch. The old code fell back to a stock Unsplash bread
  // loaf photo whenever the API returned null (or the branch photo failed to
  // load), which felt dishonest — the whole point of Willoah is that every
  // photo on the page is a real thing a real person shared. If there's
  // nothing to show, we show nothing.
  const displayBranch = branch
    ? {
        id: branch.id,
        title: branch.title,
        photo_url: branch.photo_path
          ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${branch.photo_path}`
          : null,
        category: branch.category,
        author_name: branch.author_name,
      }
    : null;
  const displayQuote = branch ? quote : null;

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-16 pb-24 overflow-x-hidden">
      {showLocationNudge && (
        <div
          data-testid="landing-location-nudge"
          className="mb-8 rounded-2xl border border-[#4E5A3D]/30 bg-[#4E5A3D]/8 p-4 flex items-start gap-3"
        >
          <MapPin className="w-4 h-4 text-[#4E5A3D] mt-0.5 shrink-0" strokeWidth={2} />
          <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <p className="text-sm text-[#2A2823]/85">
                <b>Tell us your city and the hero gets a little more local.</b>
                {" "}Willoah biases the Trusted picks toward providers near you — nothing else. Just your city, private to your account.
              </p>
            </div>
            <input
              type="text"
              data-testid="landing-location-input"
              value={locationDraft}
              onChange={(e) => setLocationDraft(e.target.value)}
              placeholder="Nashville, TN"
              className="rounded-full border border-[#B79256]/40 bg-white px-4 py-1.5 text-sm w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-[#4E5A3D]/30"
              maxLength={80}
            />
            <Button
              data-testid="landing-location-save"
              onClick={saveLocation}
              disabled={savingLocation}
              className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] h-9 px-4 text-xs shadow-none"
            >
              Save
            </Button>
          </div>
          <button
            type="button"
            onClick={dismissNudge}
            data-testid="landing-location-dismiss"
            aria-label="Dismiss"
            className="p-1 text-[#2A2823]/40 hover:text-[#2A2823] shrink-0"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-14 items-center min-w-0">
        <div className="md:col-span-7 min-w-0">
          <img
            src="/willow-logo.png"
            alt="Willoah — real life. real ideas."
            className="w-full max-w-xl -ml-1 sm:-ml-4 select-none"
            draggable={false}
          />

          {/* Hero headline + subhead pairing — leads with a short, confident
              claim, then a plain-English sentence that says who Willoah is for
              and what makes it different. The longer "what actually works…"
              sentence lives on in the meta description + How It Works intro. */}
          <h1
            data-testid="landing-headline"
            className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2A2823]"
          >
            Just what actually works.
          </h1>
          <p
            data-testid="landing-subhead"
            className="mt-5 max-w-xl text-lg text-[#2A2823]/80 leading-relaxed"
          >
            Real recipes, home finds, and recommendations — from real people, shared honestly.
          </p>

          {/* Concrete "what a branch actually is" line — REMOVED per Kelly's
              ask (Feb 23). The real seed preview card below is doing the
              tangibility job now — actual pizza / recipe / pillow beats
              another sentence. Keeping the empty block as a marker in case
              we want a tertiary line back later. */}

          {/* Compact preview of a real branch — sits right above the CTAs so
              first-time visitors see the format before they sign in. Renders
              only when the API actually returned a real branch; falling back
              to a stock stand-in would undermine the "real people, real
              recommendations" promise of the site. */}
          {displayBranch && displayBranch.id && (
            <div
              data-testid="landing-preview-branch"
              className="mt-8 w-full max-w-xl rounded-3xl border border-[#B79256]/30 bg-white/70 p-3 sm:p-4 flex items-stretch gap-4 hover:border-[#4E5A3D]/50 transition-colors overflow-hidden"
            >
              <Link
                to={`/idea/${displayBranch.id}`}
                data-testid="landing-preview-photo-link"
                className="shrink-0 rounded-2xl overflow-hidden w-24 h-24 sm:w-28 sm:h-28 bg-[#B79256]/10 flex items-center justify-center"
              >
                {displayBranch.photo_url ? (
                  <img
                    src={displayBranch.photo_url}
                    alt={displayBranch.title}
                    onError={(e) => {
                      // If the real photo fails to load (mobile Safari edge
                      // cases, cold-storage lag, etc.) drop back to a subtle
                      // sprout placeholder instead of a stock stand-in.
                      const parent = e.currentTarget.parentElement;
                      if (parent && !parent.dataset.fallbackApplied) {
                        parent.dataset.fallbackApplied = "1";
                        e.currentTarget.remove();
                        const sprout = document.createElement("div");
                        sprout.className = "text-[#4E5A3D]/60 text-2xl";
                        sprout.textContent = "🌱";
                        parent.appendChild(sprout);
                      }
                    }}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-[#4E5A3D]/60 text-2xl" aria-hidden="true">🌱</span>
                )}
              </Link>
              <div className="min-w-0 flex-1 flex flex-col justify-center">
                <div className="accent-label text-[10px] text-[#B79256]">a real seed, right now</div>
                <div className="font-serif-display text-xl text-[#2A2823] leading-tight mt-1 break-words">
                  <Link to={`/idea/${displayBranch.id}`} className="hover:text-[#4E5A3D]" data-testid="landing-preview-title-link">
                    {displayBranch.title}
                  </Link>
                </div>
                {displayQuote ? (
                  <p className="text-xs text-[#2A2823]/60 mt-1 line-clamp-2">
                    &ldquo;{displayQuote.text}&rdquo;
                    {displayQuote.author_name ? ` — ${displayQuote.author_name}` : ""}
                  </p>
                ) : (
                  <p className="text-xs text-[#2A2823]/55 mt-1 italic line-clamp-2">
                    by {displayBranch.author_name || "a Willoah member"}
                    {displayBranch.category ? ` · ${displayBranch.category}` : ""}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-4">
            {user ? (
              <Button asChild className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] px-7 h-12 text-base shadow-none">
                <Link data-testid="cta-enter-grove" to="/grove">Enter The Grove</Link>
              </Button>
            ) : (
              <Button
                asChild
                data-testid="cta-signin"
                className="rounded-full bg-[#4E5A3D] text-[#FBF8F1] hover:bg-[#3E4830] px-7 h-12 text-base shadow-none"
              >
                <Link to="/signup">Create your account</Link>
              </Button>
            )}
            {/* Browse-first CTA — visible whether signed in or not, so newcomers
                can look before they commit. */}
            {!user && (
              <>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full h-12 px-7 border-[#4E5A3D]/50 text-[#4E5A3D] hover:bg-[#4E5A3D]/10 hover:text-[#4E5A3D] bg-transparent"
                >
                  <Link data-testid="cta-login" to="/login">I already have an account</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full h-12 px-7 border-[#4E5A3D]/50 text-[#4E5A3D] hover:bg-[#4E5A3D]/10 hover:text-[#4E5A3D] bg-transparent"
                >
                  <Link data-testid="cta-browse-grove" to="/grove">Browse the Grove</Link>
                </Button>
              </>
            )}
            <Button
              asChild
              variant="outline"
              className="rounded-full h-12 px-7 border-[#B79256]/60 text-[#2A2823] hover:bg-[#B79256]/10 hover:text-[#2A2823] bg-transparent"
            >
              <Link data-testid="cta-how-it-works" to="/how-it-works">How Willoah Works</Link>
            </Button>
          </div>

          {/* Trust stats — small, understated, live */}
          <div
            data-testid="landing-trust-stats"
            className="mt-14 grid grid-cols-2 gap-6 max-w-lg border-t border-[#B79256]/25 pt-8"
          >
            <TrustStat
              testid="stat-branches"
              value={stats == null ? "…" : stats.branches.toLocaleString()}
              label={stats && stats.branches === 1 ? "honest seed planted" : "honest seeds planted"}
              hint="a seed is one honest idea — something owned, made, worn, or visited"
            />
            <TrustStat
              testid="stat-field-notes"
              value={stats == null ? "…" : stats.field_notes.toLocaleString()}
              label={stats && stats.field_notes === 1 ? "Field Note posted" : "Field Notes posted"}
              hint="a Field Note is a real user's structured review — no stars, just the truth"
            />
          </div>
        </div>

        <div className="md:col-span-5">
          {displayBranch && displayBranch.id ? (
            <div className="relative" data-testid="hero-card">
              <div className="rounded-[2rem] overflow-hidden border border-[#B79256]/25 shadow-[0_20px_60px_rgb(42,40,35,0.09)]">
                <Link to={`/idea/${displayBranch.id}`} data-testid="hero-photo-link">
                  {displayBranch.photo_url ? (
                    <img
                      src={displayBranch.photo_url}
                      alt={displayBranch.title}
                      onError={(e) => {
                        // Real photo failed — hide the img and swap in a
                        // subtle sprout tile instead of a stock photo.
                        const parent = e.currentTarget.parentElement;
                        if (parent && !parent.dataset.fallbackApplied) {
                          parent.dataset.fallbackApplied = "1";
                          e.currentTarget.remove();
                          const div = document.createElement("div");
                          div.className = "w-full h-[520px] bg-gradient-to-br from-[#B79256]/20 via-[#B79256]/8 to-[#4E5A3D]/15 flex items-center justify-center text-[#4E5A3D]/50 text-6xl";
                          div.textContent = "🌱";
                          parent.appendChild(div);
                        }
                      }}
                      className="w-full h-[520px] object-cover transition-transform duration-700 hover:scale-[1.02]"
                    />
                  ) : (
                    <div className="w-full h-[520px] bg-gradient-to-br from-[#B79256]/20 via-[#B79256]/8 to-[#4E5A3D]/15 flex items-center justify-center text-[#4E5A3D]/50 text-6xl">🌱</div>
                  )}
                </Link>
              </div>

              <button
                type="button"
                onClick={rotate}
                data-testid="hero-rotate"
                aria-label="Show a different seed"
                title="Show a different seed"
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#FBF8F1]/85 backdrop-blur border border-[#B79256]/30 flex items-center justify-center hover:bg-[#FBF8F1] transition-colors"
              >
                <RotateCw className="w-4 h-4 text-[#4E5A3D]" strokeWidth={2} />
              </button>

              <div
                data-testid="hero-field-note"
                className={`absolute -bottom-6 -left-6 bg-[#FBF8F1] rounded-2xl border border-[#B79256]/25 p-5 max-w-[260px] shadow-[0_10px_40px_rgb(42,40,35,0.09)] hidden sm:block ${displayQuote ? "" : "!hidden"}`}
              >
                <div className="accent-label text-[#B79256]">field note</div>
                <p className="font-serif-display text-lg text-[#2A2823] mt-2 leading-tight">
                  &ldquo;{displayQuote?.text}&rdquo;
                </p>
                <p className="text-xs text-[#2A2823]/60 mt-3">
                  — {displayQuote?.author_name}
                  {displayQuote?.duration ? ` · ${displayQuote.duration}` : ""}
                </p>
              </div>
            </div>
          ) : (
            // Nothing to feature (empty DB, cold start, or fetch failure).
            // Show a quiet sprout tile — never a stock photo — so the hero
            // still has visual weight without pretending to be someone else's
            // content.
            <div
              data-testid="hero-card-empty"
              className="rounded-[2rem] overflow-hidden border border-[#B79256]/25 shadow-[0_20px_60px_rgb(42,40,35,0.09)] w-full h-[520px] bg-gradient-to-br from-[#B79256]/20 via-[#B79256]/8 to-[#4E5A3D]/15 flex flex-col items-center justify-center text-[#4E5A3D]/60"
            >
              <div className="text-6xl">🌱</div>
              <p className="mt-4 font-serif-display text-xl text-[#4E5A3D]/80">A seed will be planted here soon.</p>
            </div>
          )}
        </div>
      </div>

      <section className="mt-28 grid md:grid-cols-3 gap-10">
        {[
          { title: "Every seed is proof-tagged.", body: "Owned it. Wore it. Made it. Visited it. Every idea carries a plain-English label for how the poster actually used it." },
          { title: "Field Notes, never stars.", body: "Structured reviews you'd write to a friend — the downside, who it's not for, whether you'd do it again." },
          { title: "Sponsored content is always clearly labeled.", body: "The Grove is chronological. Anything paid to appear is visibly marked so you always know what you're looking at — and brands can never buy a positive review or the review's removal." },
        ].map((f) => (
          <div key={f.title} className="border-l-2 border-[#B79256]/50 pl-6">
            <h3 className="font-serif-display text-2xl text-[#4E5A3D]">{f.title}</h3>
            <p className="text-[#2A2823]/70 mt-3 leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>
    </main>
  );
}

function TrustStat({ testid, value, label, hint }) {
  return (
    <div className="text-left" data-testid={testid}>
      <div className="font-serif-display text-4xl text-[#4E5A3D] leading-none">{value}</div>
      <div className="accent-label text-[#2A2823]/60 mt-2 leading-snug">{label}</div>
      {hint && (
        <div
          data-testid={`${testid}-hint`}
          className="text-[11px] text-[#2A2823]/50 mt-2 italic leading-snug"
        >
          {hint}
        </div>
      )}
    </div>
  );
}
